import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";
import { Icon, useDismiss } from "@cntrl/ui";
import styles from "./CommunicationPreferences.module.scss";

export interface CategoryInfoProps {
  /** The row it explains — names the button for a screen reader. */
  label: string;
  /** What the row covers, one short item each. */
  items: string[];
}

/**
 * The (i) beside a preferences row (ART-285): what the row covers, as a list. Hover shows it; a press or tap pins it
 * (touch has no hover) and a second press closes it; Escape and an outside press close it. Portalled with fixed positioning because the row sits
 * inside a scrolling dialog body that would clip it; it opens below and flips above when the bottom is tighter.
 */
export function CategoryInfo({ label, items }: CategoryInfoProps) {
  const [open, setOpen] = useState(false);
  // A press PINS it open (a second press closes it); hover only shows it while unpinned. A plain toggle would close
  // it on every mouse click, because the hover has already opened it by the time the click lands.
  const [pinned, setPinned] = useState(false);
  const close = () => {
    setOpen(false);
    setPinned(false);
  };
  const [pos, setPos] = useState<{ top: number; left: number; up: boolean } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLSpanElement>(null);
  const id = useId();

  useDismiss(open, close, rootRef);

  // Escape closes ONLY this — capture phase, stopped — so the dialog behind it stays open (the Picker rule).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      close();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const up = window.innerHeight - r.bottom < 200 && r.top > window.innerHeight - r.bottom;
    setPos({ top: up ? r.top - 6 : r.bottom + 6, left: r.left, up });
  }, [open]);

  return (
    <span
      ref={rootRef}
      className={styles.info}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => { if (!pinned) setOpen(false); }}
    >
      <button
        ref={triggerRef}
        type="button"
        className={styles.infoButton}
        aria-label={`What's in ${label}`}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onClick={() => {
          if (pinned) close();
          else {
            setPinned(true);
            setOpen(true);
          }
        }}
      >
        <Icon icon={Info} size={16} />
      </button>
      {open && pos && createPortal(
        <div
          id={id}
          role="tooltip"
          className={styles.tooltip}
          data-up={pos.up || undefined}
          style={{ top: pos.top, left: pos.left }}
        >
          <ul>
            {items.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </div>,
        document.body,
      )}
    </span>
  );
}
